import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import LoaderOverlay from './LoaderOverlay';
import IBSwitch from './IBSwitch';

interface ClientDrawerProps {
  open: boolean;
  mode: 'view' | 'edit';
  selectedUser: any;
  formData: any;
  onFormDataChange: (data: any) => void;
  onClose: () => void;
  onSave: () => Promise<void>;
  isSaving?: boolean;
}

const ClientDrawer = ({
  open,
  mode,
  selectedUser,
  formData,
  onFormDataChange,
  onClose,
  onSave,
  isSaving = false,
}: ClientDrawerProps) => {
  const handleChange = (field: string, value: string | boolean) => {
    onFormDataChange({
      ...formData,
      [field]: value,
    });
  };

  const isViewMode = mode === 'view';

  return (
    <Sheet open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <LoaderOverlay isLoading={isSaving} loadingText="Saving...">
          <SheetHeader>
            <SheetTitle>
              {isViewMode ? 'View Client Details' : 'Edit Client Details'}
            </SheetTitle>
          </SheetHeader>

          <div className="py-6 space-y-6">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Basic Information
              </h3>

              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={formData.fullName || ''}
                  onChange={e => handleChange('fullName', e.target.value)}
                  disabled={isViewMode}
                  placeholder="Enter full name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={e => handleChange('email', e.target.value)}
                  disabled={isViewMode}
                  placeholder="Enter email address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone || ''}
                  onChange={e => handleChange('phone', e.target.value)}
                  disabled={isViewMode}
                  placeholder="Enter phone number"
                />
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Account Information
              </h3>

              <div className="space-y-2">
                {isViewMode ? (
                  <>
                    <Label htmlFor="status">Status</Label>
                    <Input
                      id="status"
                      value={formData.status || ''}
                      disabled={isViewMode}
                      placeholder="Account status"
                    />
                  </>
                ) : (
                  ''
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={formData.role || ''}
                  onChange={e => handleChange('role', e.target.value)}
                  disabled={isViewMode}
                  placeholder="User role"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ibAccountHolder">IB A/C Holder</Label>
                <div className="pt-1">
                  <IBSwitch
                    checked={Boolean(formData.referralCode)}
                    disabled={isViewMode || Boolean(selectedUser?.referralCode)}
                    onCheckedChange={checked =>
                      handleChange('referralCode', checked)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <SheetFooter className="mt-6">
            {!isViewMode ? (
              <>
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={onSave} disabled={isSaving}>
                  Save Changes
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            )}
          </SheetFooter>
        </LoaderOverlay>
      </SheetContent>
    </Sheet>
  );
};

export default ClientDrawer;
